// dbManager.ts
import { log } from "console";
import Dexie from "dexie";

export interface ColumnDefinition {
  name: string;
  type: 'string' | 'number' | 'date' | 'boolean';
  isPrimaryKey?: boolean;
}

let dbInstance: Dexie | null = null;

function hashSchema(obj: any): string {
  const sortedObj = Object.keys(obj).sort().reduce((acc, key) => {
    acc[key] = obj[key];
    return acc;
  }, {} as typeof obj);
  return JSON.stringify(sortedObj);
}


export async function prepareDexieSchema(
  allTableSchemas: Record<string, ColumnDefinition[]>,
  forceVersionUpdate = false
): Promise<Dexie> {
  if (dbInstance) return dbInstance;
  // console.log('db created');

  const db = new Dexie('DynamicDB');
  const schemaHash = hashSchema(allTableSchemas);
  const previousHash = localStorage.getItem('dexie_schema_hash');
  let version = parseInt(localStorage.getItem('dexie_dynamic_version') || '1', 10);

  if (forceVersionUpdate || schemaHash !== previousHash) {
    version += 1;
    localStorage.setItem('dexie_dynamic_version', version.toString());
    localStorage.setItem('dexie_schema_hash', schemaHash);
    console.warn('🔁 Dexie version bump: Schema changed or forced update.');
  }

  const stores: Record<string, string> = {};
  for (const [tableName, columns] of Object.entries(allTableSchemas)) {
    const primaryKeys = columns.filter(col => col.isPrimaryKey).map(col => col.name);
    const otherKeys = columns.filter(col => !col.isPrimaryKey).map(col => col.name);
    const schemaStr = [...new Set([...primaryKeys, ...otherKeys])].join(',') || '++id';
    stores[tableName] = schemaStr;
  }

  db.version(version).stores(stores);
  await db.open();
  dbInstance = db;
  console.log(`🔁 Dexie schema prepared, version: ${version}`);
  return db;
}

export async function getDB(): Promise<Dexie> {
  if (!dbInstance) {
    throw new Error("⚠️ Dexie DB not initialized. Call prepareDexieSchema() first.");
  }
  return dbInstance;
}
// dexieHelpers.ts

export async function insertDataToDexie<T>(
  tableName: string,
  data: T[]
) {
  if (!Array.isArray(data) || data.length === 0) {
    console.warn(`⚠️ No data to insert for ${tableName}`);
    return;
  }

  const db = await getDB();
  await db.table<T, any>(tableName).bulkPut(data);
  console.log(`✅ Inserted ${data.length} records into "${tableName}"`);
}

export async function getDataFromDexie(tableName: string) {
  const db = await getDB();
  const data = await db.table(tableName).toArray();
  console.log(`📦 Retrieved ${data.length} records from ${tableName}`);
  return data;
}

export async function getDataFromDexiebyField(
  tableName: string,
  whereClause?: { field: string; value: any }
) {
  const db = await getDB();
  const table = db.table(tableName);

  if (whereClause) {
    const { field, value } = whereClause;

    if (value === undefined || value === null ||
        (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date) && !Array.isArray(value))) {
      console.error(`❌ Invalid key provided for table ${tableName}:`, whereClause);
      throw new TypeError('Invalid key provided. Keys must be of type string, number, Date, or Array<string | number | Date>.');
    }

    console.log(`🔍 Querying ${tableName} with field: ${field}, value: ${value}`);
    return table.where(field).equals(value).toArray();
  }
  return table.toArray();
}

export async function getFieldValuesFromDexie(
  tableName: string,
  fieldName: string
): Promise<string[]> {
  const db = await getDB();
  const table = db.table(tableName);
  const data = await table.toArray();

  return data
    .map((item: Record<string, any>) => item[fieldName])
    .filter((val) => val !== undefined && val !== null)
    .map((val) => String(val));
}

export async function getDataFromDexieArray(
  tableName: string,
  pk: string,
  whereClause?: { field: string; value: any }
): Promise<any[]> {
  const db = await getDB();
  const table = db.table(tableName);

  const rows = whereClause
    ? await table.where(whereClause.field).equals(whereClause.value).toArray()
    : await table.toArray();

  const result: any[] = [];
  rows.forEach(row => {
    result[row[pk]] = row;
  });

  return result;
}
export async function ensureDexieReady() {
  const existing = localStorage.getItem('dexie_all_schema');
  if (existing) {
    const schemas = JSON.parse(existing);
    return prepareDexieSchema(schemas);
  } else {
    console.warn("❌ No saved schema found in localStorage");
  }
}

export async function getActivePermKeysFromDexie(): Promise<string[]> {
  if (!dbInstance) {
    await ensureDexieReady();
  }

  const db = await getDB();
  
  // 🛡️ Safeguard: Check if table exists before accessing it
  if (!db.tables.find(t => t.name === 'RolePerms')) {
    console.warn("⚠️ Table RolePerms does not exist. Returning empty permissions.");
    return [];
  }

  const table = db.table('RolePerms');
  const rows = await table.toArray();

  const permKeys: string[] = rows
    .filter(row => row.PermKey && row.isActive)
    .map(row => row.PermKey);

  return permKeys;
}

export async function getFilteredPositionIds(
  tableName: string,
  orgUnitIdValue: any
): Promise<string[]> {
  const db = await getDB();
  const table = db.table(tableName);

  const data = await table
    .where('OrgUnitId')
    .equals(orgUnitIdValue)
    .toArray();
    // console.log(data, 'Filtered Position IDs');

  return data
    .map((item: Record<string, any>) => item['PositionId'])
    .filter((val) => val !== undefined && val !== null)
    .map((val) => String(val));
}
